import React, { useState, useEffect } from 'react';
import './storelocator.css';

function StoreLocator({ recipeIngredients, onStoresUpdate }) {
    // Get user data from localStorage for now (can be moved to context later)
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const userPostalCode = userData.postal_code || '';
    
    const [postalCode, setPostalCode] = useState(userPostalCode);
    const [displayedPostalCode, setDisplayedPostalCode] = useState(userPostalCode);
    const [showPostalCodeForm, setShowPostalCodeForm] = useState(false);
    const [postalCodeCoords, setPostalCodeCoords] = useState(null);
    const [, setMapCenter] = useState(null);
    const [error, setError] = useState(null);
    const [searchRadius, setSearchRadius] = useState(5);
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);

    // Get API base URL from environment variable
    const apiBaseUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';

    // Auto-load stores when component mounts if user has postal code
    useEffect(() => {
        if (userPostalCode && userPostalCode.length === 5) {
            fetchStoresForPostalCode(userPostalCode);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPostalCode, searchRadius]);

    // Handle postal code changes
    useEffect(() => {
        if (postalCode.length === 5 && postalCode !== userPostalCode) {
            fetchStoresForPostalCode(postalCode);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postalCode, searchRadius, apiBaseUrl]);
    
    const fetchStoresForPostalCode = async (code) => {
        try {
            // First get coordinates
            const coordResponse = await fetch(`${apiBaseUrl}/api/get-coordinates/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    searchItem: code
                })
            });
            
            const coordData = await coordResponse.json();
            
            if (coordData.search_coordinates) {
                const { lat, lng } = coordData.search_coordinates;
                setPostalCodeCoords([lat, lng]);
                setMapCenter([lat, lng]);
                setError(null);

                // Then fetch stores
                const storeResponse = await fetch(`${apiBaseUrl}/api/search-stores/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        searchItem: code,
                        radius: searchRadius
                    })
                });
                
                const storeData = await storeResponse.json();
                console.log('Received stores data:', storeData);
                
                if (storeData.items) {
                    const storesWithCoords = storeData.items.filter(store => 
                        store.latitude && store.longitude
                    );
                    console.log('Filtered stores with coords:', storesWithCoords);
                    setStores(storesWithCoords);
                    setDisplayedPostalCode(code);
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to get location or stores. Please try again.');
        }
    };

    // Add this separate useEffect for updating parent
    useEffect(() => {
        if (stores.length > 0) {
            onStoresUpdate(stores);
        }
    }, [stores, onStoresUpdate]);

    useEffect(() => {
        if (postalCodeCoords) {
            fetchStores();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postalCodeCoords, searchRadius, apiBaseUrl]);

    const fetchStores = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/api/search-stores/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    searchItem: postalCode,
                    radius: searchRadius
                }),
            });

            const data = await response.json();
            if (data.items) {
                setStores(data.items);
                // Remove this line from here
                // onStoresUpdate(data.items);
            }
        } catch (error) {
            console.error('Error fetching stores:', error);
            setError('Failed to fetch stores');
        }
    };

    // In your StoreLocator component, add this log before rendering the map
    console.log("Rendering StoreLocatorMap with:", { 
        postalCodeCoords, 
        storesCount: stores?.length || 0, 
        searchRadius,
        searchRadiusType: typeof searchRadius
    });

    const handlePostalCodeSubmit = () => {
        if (postalCode.length === 5) {
            setDisplayedPostalCode(postalCode);
            setShowPostalCodeForm(false);
            fetchStoresForPostalCode(postalCode);
        }
    };

    const handlePostalCodeCancel = () => {
        setPostalCode(displayedPostalCode || '');
        setShowPostalCodeForm(false);
    };

    // Handle click outside to cancel editing
    const handlePostalCodeBlur = () => {
        setTimeout(() => {
            if (showPostalCodeForm) {
                handlePostalCodeCancel();
            }
        }, 100);
    };

    return (
        <div style={{ 
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        }}>
            {/* Header with inline postal code form transition */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '15px',
                position: 'relative',
                minHeight: '32px'
            }}>
                <h3 style={{
                    fontSize: '1.4rem',
                    fontWeight: '600',
                    color: '#333',
                    margin: 0
                }}>
                    Nearby Stores
                </h3>
                
                {/* Conditional rendering: postal code pill OR inline form */}
                {(showPostalCodeForm || !displayedPostalCode) ? (
                    /* Inline Postal Code Form */
                    <input
                        type="text"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        placeholder="Enter postal code"
                        maxLength="5"
                        style={{ 
                            padding: '6px 10px',
                            fontSize: '13px',
                            borderRadius: '6px',
                            border: '1px solid #ccc',
                            width: '100px',
                            outline: 'none'
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && postalCode.length === 5) {
                                handlePostalCodeSubmit();
                            }
                            if (e.key === 'Escape') {
                                handlePostalCodeCancel();
                            }
                        }}
                        onBlur={handlePostalCodeBlur}
                        autoFocus
                    />
                ) : (
                    /* Postal Code Pill */
                    displayedPostalCode && (
                        <span
                            onClick={() => setShowPostalCodeForm(!showPostalCodeForm)}
                            style={{
                                fontSize: '0.9rem',
                                fontWeight: '500',
                                color: '#666',
                                cursor: 'pointer',
                                backgroundColor: '#f5f5f5',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                transition: 'all 0.2s ease',
                                border: '1px solid #e0e0e0'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#e8e8e8';
                                e.target.style.borderColor = '#ccc';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#f5f5f5';
                                e.target.style.borderColor = '#e0e0e0';
                            }}
                        >
                            {displayedPostalCode}
                        </span>
                    )
                )}
            </div>
            
            {/* Error message */}
            {error && (
                <div style={{ 
                    color: 'red', 
                    fontSize: '12px', 
                    marginBottom: '8px',
                    textAlign: 'right'
                }}>
                    {error}
                </div>
            )}

            {/* Radius Slider - Direct under title */}
            {displayedPostalCode && (
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                        display: 'block', 
                        marginBottom: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#666'
                    }}>
                        Search Radius: {searchRadius} km
                    </label>
                    <input
                        type="range"
                        min="1"
                        max="50"
                        value={searchRadius}
                        onChange={(e) => setSearchRadius(Number(e.target.value))}
                        style={{ width: '100%' }}
                    />
                </div>
            )}

            {/* Google Maps - Commented out for now */}
            {/*
            {postalCodeCoords && (
                <div style={{ height: '500px', width: '100%' }}>
                    <StoreLocatorMap
                        center={postalCodeCoords && postalCodeCoords.length === 2 ? 
                            {lat: postalCodeCoords[0], lng: postalCodeCoords[1]} : null}
                        stores={stores}
                        searchRadius={Number(searchRadius)}
                    />
                </div>
            )}
            */}

            {/* Store Cards Section - Scrollable */}
            {stores.length > 0 ? (
                <div style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '15px',
                    border: '1px solid #e0e0e0'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px'
                    }}>
                        {stores.map((store, index) => (
                            <div
                                key={index}
                                style={{
                                    width: '100%',
                                    minHeight: selectedStore === store ? 'auto' : '80px',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    border: selectedStore === store ? '2px solid #0B303B' : '1px solid #e0e0e0',
                                    overflow: selectedStore === store ? 'visible' : 'hidden'
                                }}
                                onClick={() => {
                                    if (selectedStore === store) {
                                        setSelectedStore(null);
                                    } else {
                                        setSelectedStore(store);
                                    }
                                }}
                            >
                                <h3 style={{
                                    margin: '0 0 4px 0',
                                    color: '#0B303B',
                                    fontWeight: '500',
                                    fontSize: selectedStore === store ? '16px' : '14px',
                                    transition: 'font-size 0.2s ease'
                                }}>
                                    {store.name}
                                </h3>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    {store.address && (
                                        <p style={{
                                            margin: '0',
                                            color: '#666',
                                            fontSize: '12px',
                                            flex: '1'
                                        }}>
                                            {store.address}
                                        </p>
                                    )}
                                    {store.distance && (
                                        <p style={{
                                            margin: '0',
                                            color: '#0B303B',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            whiteSpace: 'nowrap',
                                            marginLeft: '8px'
                                        }}>
                                            {store.distance.toFixed(1)} km
                                        </p>
                                    )}
                                </div>
                                
                                {/* Additional information shown when expanded */}
                                {selectedStore === store && (
                                    <div style={{
                                        marginTop: '12px',
                                        borderTop: '1px solid #e0e0e0',
                                        paddingTop: '12px',
                                        opacity: selectedStore === store ? 1 : 0,
                                        transition: 'opacity 0.2s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}>
                                        <div style={{ marginBottom: '12px' }}>
                                            <h4 style={{ 
                                                margin: '0 0 6px 0',
                                                color: '#0B303B',
                                                fontWeight: '500',
                                                fontSize: '14px'
                                            }}>
                                                Recipe Ingredients on Deal
                                            </h4>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '8px'
                                            }}>
                                                {recipeIngredients.map((ingredient, idx) => {
                                                    const isOnDeal = store.deals?.some(deal => 
                                                        deal.product.toLowerCase().includes(ingredient.toLowerCase())
                                                    );
                                                    
                                                    if (isOnDeal) {
                                                        return (
                                                            <div 
                                                                key={idx}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    padding: '4px 8px',
                                                                    backgroundColor: '#e3f2fd',
                                                                    borderRadius: '4px'
                                                                }}
                                                            >
                                                                <span style={{
                                                                    color: '#0B303B',
                                                                    fontWeight: '500',
                                                                    fontSize: '14px'
                                                                }}>
                                                                    {ingredient}
                                                                </span>
                                                                <span style={{
                                                                    fontSize: '12px',
                                                                    color: '#0B303B',
                                                                    fontWeight: '500',
                                                                    backgroundColor: '#e8f5e9',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '4px'
                                                                }}>
                                                                    On Deal
                                                                </span>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }).filter(Boolean)}
                                            </div>
                                        </div>
                                        
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginTop: '8px'
                                        }}>
                                            <span style={{
                                                fontSize: '14px',
                                                color: '#0B303B',
                                                fontWeight: '500'
                                            }}>
                                                {recipeIngredients.filter(ingredient => 
                                                    store.deals?.some(deal => 
                                                        deal.product.toLowerCase().includes(ingredient.toLowerCase())
                                                    )
                                                ).length} ingredients found on deal
                                            </span>
                                            {recipeIngredients.length > 0 && (
                                                <span style={{
                                                    fontSize: '14px',
                                                    color: '#0B303B',
                                                    fontWeight: '500'
                                                }}>
                                                    out of {recipeIngredients.length} needed
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : displayedPostalCode ? (
                <div style={{
                    textAlign: 'left',        
                    color: '#666',
                    fontSize: '16px'
                }}>
                    No stores found
                </div>
            ) : null}
        </div>
    );
}

export default StoreLocator; 